import { useImmer } from "use-immer";
import ApiConnector from "../connector/ApiConnector";
import { useEffect, useRef } from "react";
import UtilService from "../service/UtilService";
import { useExport } from "../context/ExportContext";
import moment from "moment";
import GetFileViaS3 from "../component/S3FileAnchor";
import DisplayedAttributesSelectorReportPage from "../component/reportPage/DisplayedAttributesConfigReportPage";
import TransactionTableReportPage from "../component/reportPage/TransactionTableReportPage";
import FilteringParametersReportPage from "../component/reportPage/FilterConfigReportPage";
import { useLocation } from "react-router-dom";
import LocationService from "../service/RouteUtilService";
import { faAnglesLeft } from "@fortawesome/free-solid-svg-icons/faAnglesLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { faAnglesRight } from "@fortawesome/free-solid-svg-icons/faAnglesRight";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faSync } from "@fortawesome/free-solid-svg-icons/faSync";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons/faArrowsRotate";

const SESSION_KEY = "reportFilterConfig";
const DECLINED_DP_SESSION_KEY = "reportDeclinedDirectPurchaseFilterConfig";

const Component = (props) => {
  const EXPORT_TO_CSV_DEFAULT_RANGE_HOURS = 3;
  const [data, updateData] = useImmer([]);
  const [displayedTransactionAttributes, updateDisplayedTransactionAttributes] =
    useImmer([]);
  const FILTER_CONFIG_TEMPLATE = {
    trnDateMax: "",
    trnDateMin: moment().subtract(7, "days").format("YYYY-MM-DDT00:00:00"),
    totalRiskMin: null,
    totalRiskMax: null,
    quantityMin: null,
    quantityMax: null,
    transactionTypes: ["A"],
    pans: [],
    merchantCodes: [],
    statuses: [],
    detailStatuses: [],
    riskEvaluationStatuses: [],
    rejectReasonCodes: [],
    transactionIds: [],
    isEstimatedFraud: false,
    isFraud: false,
    isHotspot: false,
    exportDateTimeFormat: "UTC",
    refreshSeconds: 10,
    pageSize: 30,
    pageNumber: 0,
    ewPayOnly: false,
  };
  const [filterConfig, updateFilterConfig] = useImmer(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) return { ...FILTER_CONFIG_TEMPLATE, ...JSON.parse(saved) };
    } catch (e) {
      // ignore corrupt storage
    }
    return FILTER_CONFIG_TEMPLATE;
  });
  const loadTransactionsBgJob = useRef(null);
  const filterConfigRef = useRef(filterConfig);
  const isLoadInProgressRef = useRef(false);
  const filterDetailsRef = useRef(null);
  const FILTER_DETAILS_KEY = "reportFilterDetailsOpen";
  const [isLoadInProgress, updateIsLoadInProgress] = useImmer(false);
  const location = useLocation();
  const { isExportDone, markSeen } = useExport();

  // Declined Direct Purchase section
  const [declinedData, updateDeclinedData] = useImmer([]);
  const DECLINED_DP_FILTER_CONFIG_TEMPLATE = {
    trnDateMin: moment().subtract(7, "days").format("YYYY-MM-DDT00:00:00"),
    trnDateMax: "",
    refreshSeconds: 10,
    pageSize: 30,
    pageNumber: 0,
  };
  const [declinedFilterConfig, updateDeclinedFilterConfig] = useImmer(() => {
    try {
      const saved = sessionStorage.getItem(DECLINED_DP_SESSION_KEY);
      if (saved)
        return { ...DECLINED_DP_FILTER_CONFIG_TEMPLATE, ...JSON.parse(saved) };
    } catch (e) {
      // ignore corrupt storage
    }
    return DECLINED_DP_FILTER_CONFIG_TEMPLATE;
  });
  const declinedFilterConfigRef = useRef(declinedFilterConfig);
  const declinedLoadInProgressRef = useRef(false);
  const declinedBgJob = useRef(null);
  const [declinedIsLoadInProgress, updateDeclinedIsLoadInProgress] =
    useImmer(false);

  /**
   * Defines url change — URL params override saved state; absent params fall back to saved state.
   */

  useEffect(() => {
    const saved = (() => {
      try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? { ...FILTER_CONFIG_TEMPLATE, ...JSON.parse(raw) } : null;
      } catch (e) {
        return null;
      }
    })();
    const base = saved || FILTER_CONFIG_TEMPLATE;

    const urlMerchantCodes = LocationService.getParamStringArray(location, "merchantCodes", null);
    const urlPans = LocationService.getParamStringArray(location, "pans", null);
    const urlTrnDateMin = LocationService.getParam(location, "trnDateMin", null);
    const urlTrnDateMax = LocationService.getParam(location, "trnDateMax", null);
    const urlPageSize = LocationService.getParam(location, "pageSize", null);

    const hasUrlParams = urlMerchantCodes != null || urlPans != null || urlTrnDateMin != null || urlTrnDateMax != null || urlPageSize != null;

    if (hasUrlParams) {
      const newConfig = {
        ...base,
        ...(urlMerchantCodes != null && { merchantCodes: urlMerchantCodes }),
        ...(urlPans != null && { pans: urlPans }),
        ...(urlTrnDateMin != null && { trnDateMin: urlTrnDateMin }),
        ...(urlTrnDateMax != null && { trnDateMax: urlTrnDateMax }),
        ...(urlPageSize != null && { pageSize: urlPageSize }),
      };
      // Update ref first so loadTransactions() picks up the new config immediately
      filterConfigRef.current = newConfig;
      updateFilterConfig(newConfig);
      loadTransactions();
    }
  }, [location]);

  /**
   * Defines component unmount
   */
  useEffect(() => {
    // Skip initial load if URL params are present — the location effect will handle it
    const hasUrlParams =
      LocationService.getParamStringArray(location, "merchantCodes", null) != null ||
      LocationService.getParamStringArray(location, "pans", null) != null ||
      LocationService.getParam(location, "trnDateMin", null) != null ||
      LocationService.getParam(location, "trnDateMax", null) != null ||
      LocationService.getParam(location, "pageSize", null) != null;

    if (!hasUrlParams) {
      loadTransactions();
    }
    startAutoRefreshBgJob();
    return () => {
      console.log(
        "Clearing background job #: " + loadTransactionsBgJob.current,
      );
      clearInterval(loadTransactionsBgJob.current);
      sessionStorage.removeItem(SESSION_KEY);
    };
  }, []);

  // Restore/open filter panel on mount, persist toggle state during session.
  useEffect(() => {
    const fromExport = new URLSearchParams(location.search).get("fromExport");
    const savedOpen = sessionStorage.getItem(FILTER_DETAILS_KEY);
    // fromExport always forces open; otherwise restore saved state (default: closed)
    const shouldOpen = fromExport ? true : savedOpen === "true";
    setTimeout(() => {
      if (filterDetailsRef.current) filterDetailsRef.current.open = shouldOpen;
    }, 0);
    if (isExportDone) {
      markSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterConfigRef.current = filterConfig;
    startAutoRefreshBgJob(); // Set new interval with updated config
  }, [filterConfig]);

  useEffect(() => {
    loadTransactions();
  }, [filterConfig.pageNumber]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(filterConfig));
    } catch (e) {
      // ignore storage errors (e.g. private mode quota)
    }
  }, [filterConfig]);

  const startAutoRefreshBgJob = () => {
    const bgJob = setInterval(
      () => loadTransactions(),
      filterConfigRef.current.refreshSeconds * 1000,
    );
    clearInterval(loadTransactionsBgJob.current);
    loadTransactionsBgJob.current = bgJob;
  };

  const getReportsPageFilteringParameters = () => {
    // Always read from ref to avoid stale closure issues (e.g. in setInterval callbacks or concurrent effects)
    const cfg = filterConfigRef.current;
    return {
      pageSize: cfg.pageSize,
      offset: cfg.pageNumber * cfg.pageSize,
      trnDateMin: cfg.trnDateMin ? cfg.trnDateMin + "Z" : null,
      trnDateMax: cfg.trnDateMax ? cfg.trnDateMax + "Z" : null,
      totalRiskMin: cfg.totalRiskMin,
      totalRiskMax: cfg.totalRiskMax,
      transactionTypes: cfg.transactionTypes.length > 0 ? cfg.transactionTypes : null,
      pans: cfg.pans.length > 0 ? cfg.pans : null,
      merchantCodes: cfg.merchantCodes.length > 0 ? cfg.merchantCodes : null,
      statuses: cfg.statuses.length > 0 ? cfg.statuses : null,
      detailStatuses: cfg.detailStatuses.length > 0 ? cfg.detailStatuses : null,
      riskEvaluationStatuses: cfg.riskEvaluationStatuses.length > 0 ? cfg.riskEvaluationStatuses : null,
      rejectReasonCodes: cfg.rejectReasonCodes.length > 0 ? cfg.rejectReasonCodes : null,
      transactionIds: cfg.transactionIds.length > 0 ? cfg.transactionIds : null,
      isEstimatedFraud: cfg.isEstimatedFraud ? true : null,
      isFraud: cfg.isFraud ? true : null,
      isHotspot: cfg.isHotspot ? true : null,
      ewPayOnly: cfg.ewPayOnly ? true : null,
    };
  };

  const loadTransactions = async () => {
    while (isLoadInProgressRef.current) {
      await UtilService.sleep(1);
    }
    isLoadInProgressRef.current = true;
    updateIsLoadInProgress(true);
    try {
      const transactions = await ApiConnector.getTransactions(
        getReportsPageFilteringParameters(),
      );
      updateData(transactions.data);
    } finally {
      isLoadInProgressRef.current = false;
      updateIsLoadInProgress(false);
    }
  };

  const buildExportParameters = (forceRegenerate) => {
    const trnDateMin = filterConfig.trnDateMin
      ? filterConfig.trnDateMin + "Z"
      : moment()
          .utcOffset(0)
          .subtract(EXPORT_TO_CSV_DEFAULT_RANGE_HOURS, "hours")
          .toISOString();
    const trnDateMax = filterConfig.trnDateMax
      ? filterConfig.trnDateMax + "Z"
      : moment().utcOffset(0).toISOString();

    const isMaxRangeBreached =
      moment(trnDateMax).valueOf() - moment(trnDateMin).valueOf() >
      1000 * 3600 * 24 * 186; // 6 months (6 × 31 days)

    if (isMaxRangeBreached) {
      window.alert("Maximum range for XLSX export can be 6 months.");
      return null;
    }

    let parameters = getReportsPageFilteringParameters();
    return {
      ...parameters,
      trnDateMin: trnDateMin,
      trnDateMax: trnDateMax,
      dateTimeFormat: filterConfig.exportDateTimeFormat || "LOCAL",
      forceRegenerate: forceRegenerate === true ? true : undefined,
    };
  };

  const createCsv = async () => {
    const parameters = buildExportParameters(false);
    if (!parameters) return null;
    return await ApiConnector.generateRiskEvaluationExcelToS3(parameters);
  };

  const forceCreateCsv = async () => {
    const parameters = buildExportParameters(true);
    if (!parameters) return null;
    return await ApiConnector.generateRiskEvaluationExcelToS3(parameters);
  };

  const setPageNumber = (value) => {
    updateFilterConfig((d) => {
      d.pageNumber = Number.parseInt(value);
      d.pageNumber = d.pageNumber > 500 ? 500 : d.pageNumber;
      d.pageNumber = d.pageNumber < 0 ? 0 : d.pageNumber;
    });
  };

  // --- Declined Direct Purchase section logic (independent) ---
  useEffect(() => {
    loadDeclinedTransactions();
    startDeclinedAutoRefreshBgJob();
    return () => {
      clearInterval(declinedBgJob.current);
      sessionStorage.removeItem(DECLINED_DP_SESSION_KEY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    declinedFilterConfigRef.current = declinedFilterConfig;
    startDeclinedAutoRefreshBgJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declinedFilterConfig]);

  useEffect(() => {
    loadDeclinedTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declinedFilterConfig.pageNumber]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        DECLINED_DP_SESSION_KEY,
        JSON.stringify(declinedFilterConfig),
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [declinedFilterConfig]);

  const startDeclinedAutoRefreshBgJob = () => {
    const bgJob = setInterval(
      () => loadDeclinedTransactions(),
      declinedFilterConfigRef.current.refreshSeconds * 1000,
    );
    clearInterval(declinedBgJob.current);
    declinedBgJob.current = bgJob;
  };

  const getDeclinedFilteringParameters = () => {
    const cfg = declinedFilterConfigRef.current;
    return {
      pageSize: cfg.pageSize,
      offset: cfg.pageNumber * cfg.pageSize,
      trnDateMin: cfg.trnDateMin ? cfg.trnDateMin + "Z" : null,
      trnDateMax: cfg.trnDateMax ? cfg.trnDateMax + "Z" : null,
      directPurchaseOnly: true,
      riskEvaluationStatuses: ["DECLINED", "DECLINED_BLOCK_CARD"],
      transactionTypes: null,
      ewPayOnly: null,
    };
  };

  const loadDeclinedTransactions = async () => {
    while (declinedLoadInProgressRef.current) {
      await UtilService.sleep(1);
    }
    declinedLoadInProgressRef.current = true;
    updateDeclinedIsLoadInProgress(true);
    try {
      const transactions = await ApiConnector.getTransactions(
        getDeclinedFilteringParameters(),
      );
      updateDeclinedData(transactions.data);
    } finally {
      declinedLoadInProgressRef.current = false;
      updateDeclinedIsLoadInProgress(false);
    }
  };

  const setDeclinedPageNumber = (value) => {
    updateDeclinedFilterConfig((d) => {
      d.pageNumber = Number.parseInt(value);
      d.pageNumber = d.pageNumber > 500 ? 500 : d.pageNumber;
      d.pageNumber = d.pageNumber < 0 ? 0 : d.pageNumber;
    });
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1>Report</h1>
      </header>
      <main>
        <div className="row">
          <div className="col">
            <h2>Transactions</h2>
          </div>
          <div className="col">
            <h2 className="float-right">
              <FontAwesomeIcon
                icon={faAnglesLeft}
                onClick={(e) => setPageNumber(filterConfig.pageNumber + 10)}
                className="icon-clickable"
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleLeft}
                className="icon-clickable"
                onClick={(e) => setPageNumber(filterConfig.pageNumber + 1)}
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleRight}
                className={
                  filterConfig.pageNumber === 0
                    ? "gray icon-clickable"
                    : "icon-clickable"
                }
                onClick={(e) => setPageNumber(filterConfig.pageNumber - 1)}
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAnglesRight}
                className={
                  filterConfig.pageNumber === 0
                    ? "gray icon-clickable"
                    : "icon-clickable"
                }
                onClick={(e) => setPageNumber(filterConfig.pageNumber - 10)}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              <FontAwesomeIcon
                icon={isLoadInProgress ? faSync : faArrowsRotate}
                spin={isLoadInProgress}
                className={
                  isLoadInProgress ? "gray icon-clickable" : "icon-clickable"
                }
                onClick={(e) => loadTransactions()}
              />
            </h2>
          </div>
        </div>

        <details
          ref={filterDetailsRef}
          onToggle={(e) =>
            sessionStorage.setItem(FILTER_DETAILS_KEY, e.currentTarget.open)
          }
        >
          <summary>Filtering parameters</summary>
          <FilteringParametersReportPage
            filterConfig={filterConfig}
            onChange={(e) => updateFilterConfig(e)}
          ></FilteringParametersReportPage>
          <GetFileViaS3
            caption={
              "Export to XLSX" +
              (filterConfig.trnDateMin
                ? ""
                : " (recent " + EXPORT_TO_CSV_DEFAULT_RANGE_HOURS + " hours)")
            }
            containerClassName=""
            disabled={data.length === 0}
            exportDateTimeFormat={filterConfig.exportDateTimeFormat}
            onExportDateTimeFormatChange={(fmt) =>
              updateFilterConfig((d) => {
                d.exportDateTimeFormat = fmt;
              })
            }
            filterKey={JSON.stringify({
              trnDateMin: filterConfig.trnDateMin,
              trnDateMax: filterConfig.trnDateMax,
              totalRiskMin: filterConfig.totalRiskMin,
              totalRiskMax: filterConfig.totalRiskMax,
              quantityMin: filterConfig.quantityMin,
              quantityMax: filterConfig.quantityMax,
              transactionTypes: filterConfig.transactionTypes,
              pans: filterConfig.pans,
              merchantCodes: filterConfig.merchantCodes,
              statuses: filterConfig.statuses,
              detailStatuses: filterConfig.detailStatuses,
              riskEvaluationStatuses: filterConfig.riskEvaluationStatuses,
              rejectReasonCodes: filterConfig.rejectReasonCodes,
              transactionIds: filterConfig.transactionIds,
              isEstimatedFraud: filterConfig.isEstimatedFraud,
              isFraud: filterConfig.isFraud,
              isHotspot: filterConfig.isHotspot,
              ewPayOnly: filterConfig.ewPayOnly,
              exportDateTimeFormat: filterConfig.exportDateTimeFormat,
            })}
            onFirstClick={() => createCsv()}
            onForceClick={() => forceCreateCsv()}
            onLoadFiles={() => ApiConnector.listExportFiles("risk_evaluations_")}
          ></GetFileViaS3>
          <details>
            <summary>Displayed columns</summary>
            For the current selection of transactions you can display following
            columns:
            <DisplayedAttributesSelectorReportPage
              sampleTransactions={data}
              onChange={(e) => updateDisplayedTransactionAttributes(e)}
            ></DisplayedAttributesSelectorReportPage>
          </details>
        </details>

        <TransactionTableReportPage
          transactions={data}
          displayedTransactionAttributes={displayedTransactionAttributes}
        ></TransactionTableReportPage>

        <p>Page number: {filterConfig.pageNumber}</p>

        <div className="row">
          <div className="col">
            <h2>Declined Direct Purchase Transactions</h2>
          </div>
          <div className="col">
            <h2 className="float-right">
              <FontAwesomeIcon
                icon={faAnglesLeft}
                onClick={(e) =>
                  setDeclinedPageNumber(declinedFilterConfig.pageNumber + 10)
                }
                className="icon-clickable"
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleLeft}
                className="icon-clickable"
                onClick={(e) =>
                  setDeclinedPageNumber(declinedFilterConfig.pageNumber + 1)
                }
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAngleRight}
                className={
                  declinedFilterConfig.pageNumber === 0
                    ? "gray icon-clickable"
                    : "icon-clickable"
                }
                onClick={(e) =>
                  setDeclinedPageNumber(declinedFilterConfig.pageNumber - 1)
                }
              />
              &nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faAnglesRight}
                className={
                  declinedFilterConfig.pageNumber === 0
                    ? "gray icon-clickable"
                    : "icon-clickable"
                }
                onClick={(e) =>
                  setDeclinedPageNumber(declinedFilterConfig.pageNumber - 10)
                }
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              <FontAwesomeIcon
                icon={declinedIsLoadInProgress ? faSync : faArrowsRotate}
                spin={declinedIsLoadInProgress}
                className={
                  declinedIsLoadInProgress
                    ? "gray icon-clickable"
                    : "icon-clickable"
                }
                onClick={(e) => loadDeclinedTransactions()}
              />
            </h2>
          </div>
        </div>

        <details>
          <summary>Filtering parameters</summary>
          <div className="row">
            <div className="col">
              <label>Transaction date min</label>
              <input
                type="datetime-local"
                step="1"
                value={declinedFilterConfig.trnDateMin}
                onChange={(e) =>
                  updateDeclinedFilterConfig((d) => {
                    d.trnDateMin = e.target.value;
                    d.pageNumber = 0;
                  })
                }
              />
            </div>
            <div className="col">
              <label>Transaction date max</label>
              <input
                type="datetime-local"
                step="1"
                value={declinedFilterConfig.trnDateMax}
                onChange={(e) =>
                  updateDeclinedFilterConfig((d) => {
                    d.trnDateMax = e.target.value;
                    d.pageNumber = 0;
                  })
                }
              />
            </div>
          </div>
        </details>

        <TransactionTableReportPage
          transactions={declinedData}
          displayedTransactionAttributes={displayedTransactionAttributes}
        ></TransactionTableReportPage>

        <p>Page number: {declinedFilterConfig.pageNumber}</p>
      </main>
    </>
  );
};

export default Component;
